import { Add } from "@mui/icons-material";
import { Box, Button, FormLabel, Stack, Typography } from "@mui/joy";
import { ApiQuestCostMilestone, ApiQuestDetail } from "griptonite-api-client/types";
import { FocusEvent } from "react";
import { useFieldArray, useFormContext, useWatch } from "react-hook-form";

import pluralize from "@/utils/pluralize";

import DeleteIconButton from "../DeleteIconButton";
import { NumberInput } from "../forms/TextInput";
import { QuestRulesFormValues, sortThresholds } from "./rulesForms";
import useQuestRulesCostEstimate from "./useQuestRulesCostEstimate";

const INPUTS_MAX_WIDTH = 480;

export default function QuestMilestones({ quest }: { quest: ApiQuestDetail }) {
  const { control, getValues, formState } = useFormContext<QuestRulesFormValues>();
  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: "pointsThresholds",
  });
  const thresholds = useWatch({ control, name: "pointsThresholds" });
  const { data: estimate, isFetching } = useQuestRulesCostEstimate(quest);
  const milestonesByPoints = new Map(
    (estimate?.milestones ?? []).map((milestone) => [milestone.points, milestone]),
  );

  // Reordering as the user types would make rows jump, so it waits until focus leaves the row.
  const sortOnLeave = (event: FocusEvent<HTMLElement>) => {
    if (event.currentTarget.contains(event.relatedTarget)) {
      return;
    }
    const pointsThresholds = getValues("pointsThresholds");
    const sorted = sortThresholds(pointsThresholds);
    if (sorted.some((threshold, index) => threshold !== pointsThresholds[index])) {
      replace(sorted);
    }
  };

  const listError =
    formState.errors.pointsThresholds?.root?.message ?? formState.errors.pointsThresholds?.message;

  return (
    <Box>
      <Typography level="h4">Milestones</Typography>
      <Typography level="body-sm" color="neutral" sx={{ mb: 2 }}>
        Climbers earn credits when they reach a milestone&apos;s points goal on the leaderboard.
      </Typography>
      {/* The inputs keep a narrow width, but the expected reach under each row can run wider. */}
      <Stack spacing={1}>
        <Stack direction="row" spacing={1.5} sx={{ maxWidth: INPUTS_MAX_WIDTH }}>
          <FormLabel sx={{ flex: 1 }}>Points goal</FormLabel>
          <FormLabel sx={{ flex: 1 }}>Credits</FormLabel>
          {/* Matches the delete button's width so the labels line up with the inputs. */}
          <Box sx={{ width: 32 }} />
        </Stack>
        {fields.map((milestone, index) => (
          <Stack key={milestone.id} spacing={0.5} onBlur={sortOnLeave}>
            <Stack
              direction="row"
              spacing={1.5}
              alignItems="flex-start"
              sx={{ maxWidth: INPUTS_MAX_WIDTH }}
            >
              <Box sx={{ flex: 1 }}>
                <NumberInput
                  name={`pointsThresholds.${index}.points`}
                  label={null}
                  min={1}
                  placeholder="e.g. 500"
                  aria-label={`Milestone ${index + 1} points goal`}
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <NumberInput
                  name={`pointsThresholds.${index}.creditReward`}
                  label={null}
                  min={1}
                  placeholder="e.g. 50"
                  aria-label={`Milestone ${index + 1} credits`}
                />
              </Box>
              <DeleteIconButton
                sx={{ marginTop: 0.5 }}
                disabled={fields.length < 2}
                aria-label={`Remove milestone ${index + 1}`}
                onClick={() => remove(index)}
              />
            </Stack>
            <Reach
              milestone={
                thresholds[index]?.points == null
                  ? undefined
                  : milestonesByPoints.get(thresholds[index].points)
              }
              isFetching={isFetching}
            />
          </Stack>
        ))}
        <Box>
          <Button
            variant="outlined"
            size="sm"
            startDecorator={<Add />}
            onClick={() =>
              append({
                points: null as unknown as number,
                creditReward: null as unknown as number,
              })
            }
          >
            Add milestone
          </Button>
        </Box>
      </Stack>
      {listError ? (
        <Typography level="body-sm" color="danger" sx={{ mt: 1 }}>
          {listError}
        </Typography>
      ) : null}
    </Box>
  );
}

// The share of participants the estimate expects to reach a milestone, and the median visits it
// takes them. Hidden until the row is complete and the estimate has caught up with it.
const Reach = ({
  milestone,
  isFetching,
}: {
  milestone: ApiQuestCostMilestone | undefined;
  isFetching: boolean;
}) =>
  milestone ? (
    <Typography
      level="body-sm"
      color="neutral"
      sx={{ opacity: isFetching ? 0.5 : 1, transition: "opacity 0.2s" }}
    >
      Estimate:{" "}
      <Typography fontWeight="lg">
        {milestone.shareReaching.toLocaleString(undefined, {
          style: "percent",
          maximumFractionDigits: 0,
        })}
      </Typography>{" "}
      of entries
      {milestone.medianVisits === null ? (
        " reach this"
      ) : (
        <>
          {" "}
          · about{" "}
          <Typography fontWeight="lg">{pluralize(milestone.medianVisits, "visit")}</Typography> to
          reach
        </>
      )}
    </Typography>
  ) : null;
